import {FocusEvent, KeyboardEvent, useEffect, useRef, useState} from 'react';

interface UseAutoCompleteNavigationProps {
  list: unknown[];
  direction?: 'default' | 'reverse';
  onEnterKeyUp?: (item: unknown) => void;
  onESCKeyUp?: () => void;
}

export const useAutoCompleteNavigation = <T extends HTMLElement>({
  list = [],
  direction = 'default',
  onEnterKeyUp,
  onESCKeyUp,
}: UseAutoCompleteNavigationProps) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef<T>(null);
  const isWrapperFocused = useRef(false);
  const listRefs = useRef<(HTMLDivElement | HTMLLIElement | null)[]>([]);

  // 선택된 항목으로 스크롤 이동
  const scrollToSelectedItem = (index: number) => {
    if (listRefs.current[index] && wrapperRef.current) {
      const selectedElement = listRefs.current[index];
      const container = wrapperRef.current;

      if (selectedElement) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();

        const isAbove = elementRect.top < containerRect.top;
        const isBelow = elementRect.bottom > containerRect.bottom;

        if (isAbove || isBelow) {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (list.length === 0 || !isWrapperFocused.current) {
      return;
    }

    const getNextIndex = (
      prev: number,
      key: 'ArrowDown' | 'ArrowUp',
    ): number => {
      const listLength = list.length;
      const increment = key === 'ArrowDown' ? 1 : -1;
      const adjustedIncrement =
        direction === 'default' ? increment : -increment;

      const next = prev + adjustedIncrement;

      if (next < 0) {
        return listLength - 1;
      }
      if (next >= listLength) {
        return 0;
      }
      return next;
    };

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) => {
          const newIndex = getNextIndex(prev, 'ArrowDown');
          setTimeout(() => scrollToSelectedItem(newIndex), 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) => {
          const newIndex = getNextIndex(prev, 'ArrowUp');
          setTimeout(() => scrollToSelectedItem(newIndex), 0);
          return newIndex;
        });
        break;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        if (selectedIndex >= 0 && list[selectedIndex]) {
          const item = list[selectedIndex];
          onEnterKeyUp && onEnterKeyUp(item);
          setSelectedIndex(-1);
        }
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(-1);
        isWrapperFocused.current = false;
        onESCKeyUp && onESCKeyUp();
        break;
    }
  };

  const listWrapperProps = {
    ref: wrapperRef,
    onKeyDown: handleKeyDown,
    onBlur: (e: FocusEvent) => {
      if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
        isWrapperFocused.current = false;
        setSelectedIndex(-1);
      }
    },
    tabIndex: -1,
  };

  const onInputKeyDown = (e: KeyboardEvent) => {
    if (list.length === 0 || !wrapperRef.current || e.nativeEvent.isComposing) {
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      wrapperRef.current.focus();
      if (!isWrapperFocused.current) {
        isWrapperFocused.current = true;
        setSelectedIndex(() => {
          const newIndex = 0;
          setTimeout(() => scrollToSelectedItem(newIndex), 0);
          return newIndex;
        });
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onESCKeyUp && onESCKeyUp();
    }
  };

  useEffect(() => {
    if (list.length === 0) {
      return;
    }
    setSelectedIndex(-1);
    isWrapperFocused.current = false;
    listRefs.current = new Array(list.length).fill(null);
  }, [list]);

  return {
    listWrapperProps,
    listRefs,
    selectedIndex,
    onInputKeyDown,
  };
};
